import type { TrendsView } from '../../../api/work';
import './insights.css';

/**
 * Eight weeks per lane: throughput, cycle time, backlog — and anomalies.
 *
 * **`view.patterns` was computed by this backend and rendered nowhere.**
 * That was the cheapest available tell that the Insights port was
 * incomplete, and it went unnoticed for four releases: the route returned
 * the key, `grep` found no consumer, and nobody grepped.
 *
 * Three series and the balance, on the predecessor's ordering. The
 * sparklines share no y-axis with each other — throughput is a count,
 * cycle time is days, backlog is a level — so each is scaled to its own
 * range, and the current value is printed because a shape without a number
 * cannot be compared between lanes.
 *
 * **`weekly_balance` is drawn as a zero-centred bar, not a line.** It is
 * arrivals minus departures, so its sign is the whole message: a line
 * through it invites reading the slope, and the slope of a balance is a
 * second derivative nobody asked for.
 *
 * **An anomaly marks the week it occurred, on the series it occurred in.**
 * The route reports `{week_index, metric}` and nothing about direction, so
 * this marks the point and does not claim which way it went.
 */

const SERIES: Array<{
  key: 'throughput' | 'cycle_time_days' | 'backlog_size';
  label: string;
  decimals: number;
  suffix: string;
}> = [
  { key: 'throughput', label: 'THROUGHPUT', decimals: 0, suffix: '' },
  { key: 'cycle_time_days', label: 'CYCLE TIME', decimals: 1, suffix: 'd' },
  { key: 'backlog_size', label: 'BACKLOG', decimals: 0, suffix: '' },
];

const W = 104;
const H = 20;

function path(series: number[]): string {
  const finite = series.filter((v) => Number.isFinite(v));
  if (finite.length === 0) return '';
  const min = Math.min(...finite);
  const max = Math.max(...finite);
  const span = max - min || 1;
  return series
    .map((v, i) => {
      const x = series.length === 1 ? W / 2 : (i / (series.length - 1)) * W;
      const y = H - ((v - min) / span) * (H - 2) - 1;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

function fmt(v: number | undefined, decimals: number, suffix: string): string {
  if (v === undefined || !Number.isFinite(v)) return '–';
  return `${v.toFixed(decimals)}${suffix}`;
}

export function LaneHealthCards({ view }: { view: TrendsView }) {
  const patterns = view.patterns;
  if (!patterns || patterns.lanes.length === 0) {
    return (
      <p className="in-dim">
        No weekly pattern stored. <code>work.trends.snapshot</code> assembles
        it with the rest of the view, so this is empty only before its first
        run.
      </p>
    );
  }
  const labels = Object.fromEntries(
    view.lanes.map((l) => [l.category, { label: l.label, color: l.color }]),
  );

  return (
    <section className="lhc">
      <span className="nb__label">
        Eight weeks by lane · {patterns.weeks.length} points
      </span>
      <ul className="lhc__cards">
        {patterns.lanes.map((lane) => {
          const meta = labels[lane.category];
          const anomalies = lane.anomalies ?? [];
          return (
            <li
              className="lhc__card"
              key={lane.category}
              style={{ ['--lane' as string]: meta?.color ?? 'currentColor' }}
            >
              <div className="lhc__head">
                <span className="lhc__name">
                  {meta?.label ?? lane.category.toUpperCase()}
                </span>
                {anomalies.length > 0 && (
                  <span
                    className="in-badge in-badge--attend"
                    title={anomalies
                      .map(
                        (a) =>
                          `${a.metric} in week ${a.week_index + 1} of ${patterns.weeks.length}`,
                      )
                      .join('\n')}
                  >
                    {anomalies.length} anomal
                    {anomalies.length === 1 ? 'y' : 'ies'}
                  </span>
                )}
              </div>

              {SERIES.map((s) => {
                const series = lane[s.key] ?? [];
                const current = series[series.length - 1];
                const flagged = anomalies.some((a) => a.metric === s.key);
                return (
                  <div className="lhc__row" key={s.key}>
                    <span className="lhc__label">{s.label}</span>
                    <svg
                      className={`lhc__spark${flagged ? ' is-flagged' : ''}`}
                      viewBox={`0 0 ${W} ${H}`}
                      preserveAspectRatio="none"
                      aria-hidden
                    >
                      <path d={path(series)} fill="none" strokeWidth="1.3" />
                    </svg>
                    <span className="lhc__value">
                      {fmt(current, s.decimals, s.suffix)}
                    </span>
                  </div>
                );
              })}

              <div className="lhc__row">
                <span className="lhc__label" title="arrivals minus departures">
                  BALANCE
                </span>
                <span className="lhc__balance" aria-hidden>
                  {(lane.weekly_balance ?? []).map((v, i) => (
                    <span
                      key={i}
                      className={`lhc__bar${v > 0 ? ' is-up' : v < 0 ? ' is-down' : ''}`}
                      style={{
                        ['--mag' as string]: String(
                          Math.min(1, Math.abs(v) / 5),
                        ),
                      }}
                      title={`week ${i + 1}: ${v > 0 ? '+' : ''}${v}`}
                    />
                  ))}
                </span>
                <span className="lhc__value">
                  {fmt(
                    (lane.weekly_balance ?? [])[
                      (lane.weekly_balance ?? []).length - 1
                    ],
                    0,
                    '',
                  )}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
